/* eslint-disable no-console */
import {SearchOutlined} from '@ant-design/icons';
import {Table as TableAnt, InputRef, Input} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {ColumnType} from 'antd/es/table';
import React, {useMemo, useRef} from 'react';

import {emissionUnits} from '../../../components/Forms/interface/unitChange';
import {defaultCol} from '../../../components/Table/Interface/ITable';
import {ISelect} from '../../../interface/ISelect';
import {formatIndian} from '../../../utils/formatIndian';

import {toDecimalString} from './common';
import {DataLineageLabel, DataLineageKey} from './DataLineageKeyLabel';
import type {DataLineageEFCell, DataLineageRow} from './dataLineageTypes';

const DataLineageTable: React.FC<{mainData: DataLineageRow[]}> = ({
  mainData
}) => {
  const searchInput = useRef<InputRef>(null);

  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const columns = useMemo<ColumnsType<DataLineageRow>>(
    () => [
      {
        title: DataLineageLabel.GROUP,
        dataIndex: DataLineageKey.GROUP,
        type: 'select',
        ...SearchFilter(
          DataLineageLabel.GROUP,
          DataLineageKey.GROUP,
          mainData?.map(
            (item) =>
              ({
                label: item.group,
                value: item.group
              }) as any
          )
        )
      },
      {
        title: DataLineageLabel.INPUT_DATA,
        dataIndex: DataLineageKey.INPUT_DATA,
        render: (inputData: string) => {
          const [value, unit] = inputData.split(' ');

          return `${formatIndian(value)} ${unit}`;
        }
      },
      {
        title: DataLineageLabel.SI_DATA,
        dataIndex: DataLineageKey.SI_DATA,
        render: (inputData: string) => {
          const [value, unit] = inputData.split(' ');

          return `${formatIndian(value)} ${unit}`;
        }
      },
      {
        title: DataLineageLabel.EF_DATA,
        dataIndex: DataLineageKey.EF_DATA,
        key: DataLineageKey.EMISSION_FACTOR,
        render: (_: unknown, record: DataLineageRow) => {
          const eFData = record[DataLineageKey.EF_DATA] as
            | DataLineageEFCell
            | undefined;

          return eFData?.emissionFactor && eFData?.efUnit
            ? `x ${toDecimalString(Number(eFData.emissionFactor))} ${
                eFData.efUnit
              }`
            : '-';
        }
      },
      {
        title: DataLineageLabel.URL_NAME,
        dataIndex: DataLineageKey.EF_DATA,
        key: DataLineageKey.EMISSIONS_FACTOR_SOURCE,
        render: (_: unknown, record: DataLineageRow) => {
          const eFData = record[DataLineageKey.EF_DATA] as
            | DataLineageEFCell
            | undefined;

          return eFData?.url ? (
            <a href={eFData.url} target="_blank" rel="noopener noreferrer">
              {eFData.urlName}
            </a>
          ) : (
            eFData?.urlName ?? '-'
          );
        }
      },
      {
        title: DataLineageLabel.YEARLY_EMISSIONS,
        dataIndex: DataLineageKey.YEARLY_EMISSIONS,
        render: (yearlyEmissions: number | string | undefined) =>
          `${formatIndian(yearlyEmissions ?? 0)} ${emissionUnits.T_CO2e}`
      }
    ],
    []
  );

  return (
    <div>
      <TableAnt
        dataSource={mainData}
        columns={columns}
        rowKey={(record, index) =>
          `${String(record.group ?? '')}-${String(
            record.dataLabel ?? ''
          )}-${index}`
        }
        locale={{
          emptyText: 'No carbon accounting data found for this organization.'
        }}
      />
    </div>
  );
};

export default DataLineageTable;
